import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api, type ApiError } from '../../api/client';
import { Alert, Field } from '../../components/Field';
import { StatusPill } from '../../components/StatusPill';
import { ConfirmDialog, type PendingDelete } from '../../components/ConfirmDialog';
import { useAuth } from '../../auth/AuthContext';
import { duration, money } from '../../lib/format';

interface CourseRow {
  id: string;
  title: string;
  status: string;
  difficulty: string;
  category: string;
  moduleCount: number;
  durationMinutes: number;
  priceMinor: number;
  currency: string;
  isFree: boolean;
  instructors: string[];
  updatedAt: string;
  thumbnailUrl: string | null;
}

interface Option {
  id: string;
  name: string;
}

const STATUSES = ['', 'DRAFT', 'REVIEW', 'APPROVED', 'PUBLISHED', 'ARCHIVED'];

/**
 * Rows fetched at a time. The API caps a page at 100.
 *
 * The list used to ask for no page size at all, got the API's default of 25,
 * and showed those 25 as if they were everything — the header counted the
 * rows on screen, so a platform with 36 courses said "25 courses" and the
 * other eleven could not be reached from here.
 */
const PAGE_SIZE = 50;

interface CoursePage {
  items: CourseRow[];
  page: number;
  totalItems: number;
  totalPages: number;
}

export function AdminCourses() {
  const [rows, setRows] = useState<CourseRow[] | null>(null);
  /** How many courses match, across every page — not how many are on screen. */
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  /** What is typed, and what was last searched for once typing paused. */
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  /** Only the newest request may fill the table; an older one can land last. */
  const latest = useRef(0);
  const [categories, setCategories] = useState<Option[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', categoryId: '', shortDescription: '' });
  // Deleting a course removes the page that did it, so the confirmation has
  // to be carried here instead of shown there.
  const arrived = (useLocation().state as { notice?: string } | null)?.notice ?? null;
  const [notice, setNotice] = useState<string | null>(arrived);
  /** The deletion waiting to be confirmed, or none. */
  const [pending, setPending] = useState<PendingDelete | null>(null);
  // The API checks this too; hiding the button just saves a refusal.
  const canDelete = useAuth().can('COURSE_DELETE');

  function fetchPage(number: number) {
    const params = new URLSearchParams({ page: String(number), pageSize: String(PAGE_SIZE) });
    if (status) params.set('status', status);
    if (query) params.set('search', query);
    return api<CoursePage>(`/admin/courses?${params.toString()}`);
  }

  /** The first page again: after a filter changes, or a course is added or removed. */
  function load() {
    const request = ++latest.current;
    fetchPage(1)
      .then((result) => {
        if (request !== latest.current) return;
        setRows(result.items);
        setTotal(result.totalItems);
        setPage(1);
        setTotalPages(result.totalPages);
      })
      .catch((caught) => setError(caught as ApiError));
  }

  async function loadMore() {
    if (loadingMore || page >= totalPages) return;
    const request = latest.current;
    setLoadingMore(true);
    try {
      const result = await fetchPage(page + 1);
      // A filter changed while this was in flight; its rows belong to a
      // list that is no longer on screen.
      if (request !== latest.current) return;
      setRows((current) => [...(current ?? []), ...result.items]);
      setTotal(result.totalItems);
      setPage(result.page);
      setTotalPages(result.totalPages);
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(load, [status, query]);

  // Searched once typing pauses, not on every keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    api<{ items: Array<{ id: string; name: string }> }>('/admin/catalog/categories')
      .then((result) => setCategories(result.items))
      .catch(() => setCategories([]));
  }, []);

  async function create(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await api('/admin/courses', { method: 'POST', body: JSON.stringify(form) });
      setForm({ title: '', categoryId: '', shortDescription: '' });
      setCreating(false);
      load();
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  async function remove(path: string, message: string) {
    setError(null);
    setNotice(null);
    try {
      await api(path, { method: 'DELETE' });
      setNotice(message);
      load();
    } catch (caught) {
      // Refused once anyone has enrolled or earned a certificate; the API's
      // message says so and points at Unpublish.
      setError(caught as ApiError);
    }
  }

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">Courses</h1>
        <p className="page__lede">
          Every course in any state. An instructor sees only the courses they are assigned to.
        </p>
      </div>

      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}
      {notice ? <Alert kind="success" title={notice} /> : null}

      {pending ? (
        <ConfirmDialog
          pending={pending}
          onCancel={() => setPending(null)}
          onConfirm={async () => {
            const { path, message } = pending;
            setPending(null);
            await remove(path, message);
          }}
        />
      ) : null}

      {creating ? (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card__head">
            <span className="card__title">New course</span>
          </div>
          <div className="card__body">
            <form onSubmit={create}>
              <Field
                label="Title"
                required
                maxLength={250}
                value={form.title}
                onChange={(event) => setForm({ ...form, title: event.target.value })}
              />
              <div className="field">
                <label className="field__label" htmlFor="cat">
                  Category
                </label>
                <select
                  id="cat"
                  className="field__input"
                  required
                  value={form.categoryId}
                  onChange={(event) => setForm({ ...form, categoryId: event.target.value })}
                >
                  <option value="">Choose a category…</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <Field
                label="Short description"
                required
                hint="One sentence, shown on the course card."
                value={form.shortDescription}
                onChange={(event) => setForm({ ...form, shortDescription: event.target.value })}
              />
              <div className="row">
                <button className="btn" type="submit">
                  Create draft
                </button>
                <button className="btn btn--ghost" type="button" onClick={() => setCreating(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="card__head">
          <span className="card__title">
            {rows
              ? `${total} course${total === 1 ? '' : 's'}${
                  rows.length < total ? ` · showing ${rows.length}` : ''
                }`
              : 'Loading…'}
          </span>
          <div className="row" style={{ alignItems: 'center' }}>
            <input
              className="field__input"
              type="search"
              aria-label="Search courses by title"
              placeholder="Search by title…"
              maxLength={200}
              style={{ width: 200 }}
              value={search}
              onChange={(event) => {
                setNotice(null);
                setSearch(event.target.value);
              }}
            />
            <select
              className="cat__sort"
              aria-label="Filter by status"
              value={status}
              onChange={(event) => {
                // The notice describes what just happened, not what is now
                // on screen; changing the filter moves past it.
                setNotice(null);
                setStatus(event.target.value);
              }}
            >
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value === '' ? 'All statuses' : value}
                </option>
              ))}
            </select>
            {/* The guided route is the default: it ends with a course that
                passes the publish check, where the bare form ends with a
                draft and a list of blockers to go and find. The form stays
                for someone who only wants the shell. */}
            <Link className="btn btn--sm" to="/admin/courses/new">
              New course
            </Link>
            <button className="btn btn--ghost btn--sm" onClick={() => setCreating(true)}>
              Blank draft
            </button>
          </div>
        </div>

        {!rows ? (
          <div className="spinner">Loading courses…</div>
        ) : rows.length === 0 ? (
          <div className="empty">
            {status || query
              ? 'No courses match these filters.'
              : 'No courses yet. Create one to get started.'}
          </div>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>Course</th>
                <th>Category</th>
                <th>Status</th>
                <th>Structure</th>
                <th>Price</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((course) => (
                <tr key={course.id}>
                  <td>
                    <div className="rowcover">
                      {course.thumbnailUrl ? (
                        <img
                          className="rowcover__img"
                          src={course.thumbnailUrl}
                          alt=""
                          loading="lazy"
                        />
                      ) : (
                        // Not an empty box: "no cover" is the thing that stops
                        // a course publishing, so it should read as missing.
                        <span className="rowcover__img rowcover__img--none" aria-hidden="true">
                          ✕
                        </span>
                      )}
                      <span>
                        <strong>{course.title}</strong>
                        <br />
                        <span className="muted" style={{ fontSize: 12 }}>
                          {course.instructors.join(', ') || 'No instructor assigned'}
                        </span>
                      </span>
                    </div>
                  </td>
                  <td>{course.category}</td>
                  <td>
                    <StatusPill status={course.status} />
                  </td>
                  <td className="muted">
                    {course.moduleCount} module{course.moduleCount === 1 ? '' : 's'} ·{' '}
                    {duration(course.durationMinutes)}
                  </td>
                  <td>{course.isFree ? 'Free' : money(course.priceMinor, course.currency)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Link className="btn btn--ghost btn--sm" to={`/admin/courses/${course.id}`}>
                      Open builder
                    </Link>
                    {canDelete ? (
                      <button
                        className="iconbtn iconbtn--danger"
                        type="button"
                        style={{ marginLeft: 8, verticalAlign: 'middle' }}
                        aria-label={`Delete ${course.title}`}
                        title="Delete course"
                        onClick={() =>
                          setPending({
                            path: `/admin/courses/${course.id}`,
                            title: 'Delete this course?',
                            detail: `“${course.title}”, its ${course.moduleCount} module${
                              course.moduleCount === 1 ? '' : 's'
                            } and everything in them will be removed from the platform. If anyone has enrolled or earned a certificate, this is refused — unpublish it instead.`,
                            message: `“${course.title}” deleted.`,
                          })
                        }
                      >
                        Delete
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {rows && rows.length < total ? (
          <div className="listmore">
            <span>
              Showing {rows.length} of {total}
            </span>
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              disabled={loadingMore}
              onClick={() => void loadMore()}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}
